<template>
  <div
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    @click.self="$emit('cancel')"
  >
    <div class="bg-white rounded-xl shadow-xl max-w-md w-full p-6 flex flex-col items-center gap-6">
      <!-- Key Icon -->
      <svg class="w-16 h-16" viewBox="0 0 24 24" fill="none" style="color: var(--team-primary);">
        <path d="M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" fill="currentColor"/>
      </svg>

      <!-- Text Content -->
      <div class="flex flex-col gap-4 text-center">
        <h3 class="text-xl font-bold text-gray-900">
          Admin Pin Required
        </h3>
        <p class="text-base font-medium text-gray-600 leading-6">
          Please enter the admin PIN to manage kiosk settings. This ensures only authorized staff can make changes.
        </p>
      </div>

      <!-- PIN Input Boxes -->
      <div class="flex gap-2 items-center">
        <div
          v-for="(digit, index) in 4"
          :key="index"
          class="relative w-16 h-20"
        >
          <!-- The input keeps the real digit but renders it (and its caret)
               invisible. On slow webviews -webkit-text-security paints the digit
               first and the disc a frame later, so the number is briefly
               readable. Transparent text never paints the digit at all; the mask
               dot below is drawn from state, so only a dot is ever visible. -->
          <input
            :ref="'pinInput' + index"
            type="tel"
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="1"
            style="color: transparent; caret-color: transparent;"
            class="w-full h-full bg-gray-100 text-center text-5xl font-normal rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 transition-all"
            :class="{ 'ring-2 ring-red-500': error }"
            :value="pinDigits[index]"
            @input="handleInput($event, index)"
            @keydown="handleKeydown($event, index)"
            @paste="handlePaste"
          />
          <span
            v-if="pinDigits[index]"
            class="pointer-events-none absolute inset-0 flex items-center justify-center"
          >
            <span class="w-4 h-4 rounded-full bg-gray-900"></span>
          </span>
        </div>
      </div>

      <!-- Error Message -->
      <p v-if="error" class="text-red-500 text-sm text-center -mt-4">
        Incorrect PIN. Please try again.
      </p>

      <!-- Buttons -->
      <div class="flex flex-col gap-4 w-52">
        <button
          @click="verifyPin"
          :disabled="!isPinComplete || verifying"
          class="w-full px-6 py-2 rounded-lg font-semibold text-base text-white transition-opacity"
          :class="(isPinComplete && !verifying) ? '' : 'opacity-50 cursor-not-allowed'"
          :style="confirmButtonStyle"
        >
          <span v-if="verifying">Verifying...</span>
          <span v-else>Confirm</span>
        </button>
        <button
          @click="$emit('cancel')"
          class="w-full px-6 py-2 rounded-lg font-semibold text-base border transition-colors hover:bg-gray-50"
          style="border-color: var(--team-primary); color: var(--team-primary);"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AdminPinModal',
  emits: ['verified', 'cancel'],
  data() {
    return {
      pinDigits: ['', '', '', ''],
      error: false,
      verifying: false,
    };
  },
  computed: {
    isPinComplete() {
      return this.pinDigits.every((digit) => digit !== '');
    },
    confirmButtonStyle() {
      if (this.isPinComplete) {
        return {
          backgroundColor: 'var(--team-primary)',
        };
      }
      return {
        backgroundColor: '#bcccdc',
      };
    },
  },
  mounted() {
    // Focus the first input when modal opens
    this.$nextTick(() => {
      const firstInput = this.$refs.pinInput0;
      if (firstInput && firstInput[0]) {
        firstInput[0].focus();
      }
    });
  },
  methods: {
    handleInput(event, index) {
      const value = event.target.value.replace(/\D/g, ''); // Only digits

      if (value.length > 0) {
        this.$set(this.pinDigits, index, value.charAt(0));
        this.error = false;

        // Move to next input or auto-submit if complete
        if (index < 3) {
          const nextInput = this.$refs[`pinInput${index + 1}`];
          if (nextInput && nextInput[0]) {
            nextInput[0].focus();
          }
        } else {
          // Auto-submit when 4th digit is entered
          this.$nextTick(() => {
            if (this.isPinComplete) {
              this.verifyPin();
            }
          });
        }
      } else {
        this.$set(this.pinDigits, index, '');
      }
    },
    handleKeydown(event, index) {
      // Handle backspace
      if (event.key === 'Backspace') {
        if (this.pinDigits[index] === '' && index > 0) {
          // If current is empty, move to previous and clear it
          const prevInput = this.$refs[`pinInput${index - 1}`];
          if (prevInput && prevInput[0]) {
            this.$set(this.pinDigits, index - 1, '');
            prevInput[0].focus();
          }
        } else {
          this.$set(this.pinDigits, index, '');
        }
        this.error = false;
      }

      // Handle Enter
      if (event.key === 'Enter' && this.isPinComplete) {
        this.verifyPin();
      }
    },
    handlePaste(event) {
      event.preventDefault();
      const pastedData = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);

      for (let i = 0; i < 4; i++) {
        this.$set(this.pinDigits, i, pastedData[i] || '');
      }

      this.error = false;

      // Auto-submit if complete PIN was pasted, otherwise focus next empty input
      if (pastedData.length === 4) {
        this.$nextTick(() => {
          if (this.isPinComplete) {
            this.verifyPin();
          }
        });
      } else {
        const lastFilledIndex = Math.min(pastedData.length, 4) - 1;
        if (lastFilledIndex >= 0 && lastFilledIndex < 3) {
          const nextInput = this.$refs[`pinInput${lastFilledIndex + 1}`];
          if (nextInput && nextInput[0]) {
            nextInput[0].focus();
          }
        }
      }
    },
    verifyPin() {
      if (!this.isPinComplete || this.verifying) return;

      const enteredPin = this.pinDigits.join('');
      this.verifying = true;

      // Emit the entered PIN to parent for server-side verification
      this.$emit('verified', enteredPin);
    },
    showError() {
      // Called by parent when server-side PIN verification fails
      this.error = true;
      this.verifying = false;
      this.pinDigits = ['', '', '', ''];
      // Focus first input
      this.$nextTick(() => {
        const firstInput = this.$refs.pinInput0;
        if (firstInput && firstInput[0]) {
          firstInput[0].focus();
        }
      });
    },
    resetState() {
      // Reset modal state when closing
      this.pinDigits = ['', '', '', ''];
      this.error = false;
      this.verifying = false;
    },
  },
};
</script>
